import {
  Divider,
  HStack,
  Icon,
  Skeleton,
  Text,
  useDisclosure,
  VStack,
} from '@chakra-ui/react';
import { format, getDaysInMonth, lastDayOfMonth } from 'date-fns';
import React from 'react';
import { FiEdit2, FiSearch } from 'react-icons/fi';
import { useQueryClient } from 'react-query';
import { Navigate, useSearchParams } from 'react-router-dom';
import { Column } from 'react-table';

import { AppError } from '../../../../framework/models/errors';
import { TypeUtils } from '../../../../framework/utils/UtilityTypes';
import { useCheckArea } from '../../../components/Check';
import ClickableText from '../../../components/ClickableText';
import { BackButton, DecideButton } from '../../../components/StyledButton';
import StyledTable from '../../../components/StyledTable';
import { pageKeywords, pageNames, pagePaths } from '../../../constants/Page';
import { useGlobalStateMachine } from '../../../GlobalStore';
import {
  DateChangerState,
  generateInitialState,
  getStateFromDate,
  useDateChangers,
} from '../../../hooks/DateChangers';
import { useEngeneNavigate } from '../../../hooks/navigate';
import {
  ASSIGNED_MATTER_CONTEXT_NAMES,
  usePostAssignedMatterSearch,
} from '../../../hooks/queries/assignedMatter';
import { usePostDeadlineSearch } from '../../../hooks/queries/deadline';
import { usePostWorkApplyApply } from '../../../hooks/queries/workApply';
import useToastHook from '../../../hooks/ToastHook';
import { AssignedMatterSearchResult } from '../../../models/gen/assignedMatterSearchResult';
import { DeadlineMode } from '../../../models/gen/deadlineMode';
import { HalfPeriod } from '../../../models/gen/halfPeriod';
import { MasterKey } from '../../../models/gen/masterKey';
import { WorkApply } from '../../../models/gen/workApply';
import { Formatter } from '../../../utils/FormatUtils';
import { useMasterContext } from '../../MasterProvider';
import { MatterDetailModal } from '../../modals/MatterDetail';
import { useSizeContext } from '../../SizeProvider';
import Wireframe from '../../wireframe/Wireframe';

type DatasourceType = {
  isApproved?: boolean;
  url?: string;
  date: string;
  matterId?: number;
  matterName?: string;
  customerName?: string;
  workTime?: string;
  workContent?: string;
  point?: string;
  remarks?: string;
};

/**
 * 勤怠申請画面
 *
 * - URL `/attendance-report`
 * - パラメータ: なし
 * - クエリ: なし
 *
 * @returns
 */
const AttendanceReport: React.FunctionComponent = () => {
  const { actions, state } = useGlobalStateMachine();
  if (TypeUtils.isNil(state.userInfo)) {
    return <Navigate to={pagePaths.ROOT} />;
  }

  const queryClient = useQueryClient();
  const setAssignedMatter = React.useCallback(
    (result: AssignedMatterSearchResult) =>
      queryClient.setQueryData(
        ASSIGNED_MATTER_CONTEXT_NAMES.POST_ASSIGNED_MATTER_SEARCH,
        result
      ),
    [queryClient]
  );
  const data = queryClient.getQueryData<AssignedMatterSearchResult | undefined>(
    ASSIGNED_MATTER_CONTEXT_NAMES.POST_ASSIGNED_MATTER_SEARCH
  );
  const [workApply, setWorkApply] = React.useState<WorkApply | undefined>(
    undefined
  );

  const master = useMasterContext();
  const sizes = useSizeContext();
  const navigate = useEngeneNavigate();
  const [searchParams] = useSearchParams();
  const { CheckArea, checked, off } = useCheckArea();
  const [selectedMatterId, setSelectedMatterId] = React.useState<number>();
  const { isOpen, onClose, onOpen } = useDisclosure();

  const applyUnits = React.useMemo(
    () => ({
      1: master.applyUnit.attendance1,
      2: master.applyUnit.attendance2,
    }),
    [master.applyUnit]
  );
  const leftDeadend = React.useMemo(() => {
    if (!TypeUtils.isNilString(state.appConfig.facilitySettings?.PAST_LIMIT)) {
      const date = new Date();
      date.setMonth(
        date.getMonth() +
          (state.appConfig.facilitySettings!.PAST_LIMIT as number)
      );
      const startDay = master.applyUnit.attendance1?.startDay;
      if (TypeUtils.isNilNumber(startDay)) return undefined;
      date.setDate(startDay);
      return getStateFromDate(applyUnits, date);
    }
    if (!TypeUtils.isNilString(state.appConfig.facilitySettings?.START_DATE)) {
      return getStateFromDate(
        applyUnits,
        Formatter.createDate(
          state.appConfig.facilitySettings!.START_DATE as string
        )
      );
    }
    return undefined;
  }, [state.appConfig.facilitySettings]);
  const rightDeadend = React.useMemo(() => {
    if (
      !TypeUtils.isNilString(state.appConfig.facilitySettings?.FUTURE_LIMIT)
    ) {
      let date = new Date();
      date.setMonth(
        date.getMonth() +
          (state.appConfig.facilitySettings!.FUTURE_LIMIT as number)
      );
      const endDay = master.applyUnit.attendance2?.endDay;
      if (TypeUtils.isNilNumber(endDay)) return undefined;
      if (endDay === 99) {
        date = lastDayOfMonth(date);
      } else date.setDate(endDay);
      return getStateFromDate(applyUnits, date);
    }
    return undefined;
  }, [state.appConfig.facilitySettings]);
  const {
    HalfMonthChanger,
    setState,
    state: halfMonthState,
  } = useDateChangers({
    leftDeadend,
    rightDeadend,
    initialState:
      generateInitialState(searchParams, ['year', 'month', 'half']) ||
      state.pageInfo.ATTENDANCE_REPORT?.dateState,
    applyUnits,
  });
  React.useEffect(() => {
    setWorkApply(
      data?.reference?.WorkApplies?.find(
        (wa) =>
          wa.year === halfMonthState.year && wa.month === halfMonthState.month
      )
    );
  }, [data, halfMonthState]);

  // APIs
  const postAssignedMatterSearch = usePostAssignedMatterSearch();
  const postWorkApplyApply = usePostWorkApplyApply();
  const { data: deadlines, refetch: refetchDeadlines } = usePostDeadlineSearch({
    modes: [DeadlineMode.Attendance],
  });
  const { toast } = useToastHook();

  const onChangeChanger = (dateState: DateChangerState) => {
    actions.updatePageInfo({ ATTENDANCE_REPORT: { dateState } });
    off();
    postAssignedMatterSearch.mutate(
      {
        year: dateState.year,
        month: dateState.month,
        half:
          dateState.half === 1 ? HalfPeriod.FirstHalf : HalfPeriod.SecondHalf,
      },
      {
        onSuccess: (result) => {
          if (!TypeUtils.isNil(result)) {
            setAssignedMatter(result);
          }
        },
        onError: (error) =>
          toast(
            error instanceof AppError ? error : '勤怠状況の取得に失敗しました'
          ),
      }
    );
  };

  React.useState(() => {
    refetchDeadlines();
    onChangeChanger(halfMonthState);
  });
  React.useEffect(() => {
    const newState =
      generateInitialState(searchParams, ['year', 'month', 'half']) ||
      state.pageInfo.ATTENDANCE_REPORT?.dateState;
    onChangeChanger(newState);
    setState(newState);
  }, [searchParams]);

  const datasource: Array<DatasourceType> = React.useMemo(
    () =>
      (data?.results || []).flatMap(
        ({ AssignedDailyMatterSummaries: summaries, date, workApplyId }) =>
          TypeUtils.isNil(summaries) || summaries.length === 0
            ? [
                {
                  date: Formatter.dateOfWeekString(date),
                },
              ]
            : summaries.map((summary, _, array) => {
                const index = array
                  .filter((e) => e.userCodeWorker === summary.userCodeWorker)
                  .map((e) => e.assignWorkerId)
                  .indexOf(summary.assignWorkerId);
                return {
                  url: TypeUtils.isNilString(summary.matterName)
                    ? undefined
                    : `${pagePaths.WORK_CONTENT}/${date}/${
                        summary.userCodeWorker
                      }/${
                        TypeUtils.isNonNilNumber(workApplyId) &&
                        TypeUtils.isNonNilString(
                          data?.reference?.WorkApplies?.find(
                            (wa) => wa.workApplyId === workApplyId
                          )?.applyTime
                        )
                          ? pageKeywords.mode.VIEW
                          : pageKeywords.mode.EDIT
                      }${
                        state.userInfo?.roleOptions.isOutsourcer
                          ? `?outsourcerCode=${state.userInfo?.outsourcer_code}&index=${index}`
                          : `?index=${index}`
                      }`,
                  date: Formatter.dateOfWeekString(date),
                  matterId: summary.matterId,
                  matterName: summary.matterName,
                  customerName: summary.customerName,
                  isApproved:
                    TypeUtils.isNonNilNumber(workApplyId) &&
                    TypeUtils.isNonNilString(
                      data?.reference?.WorkApplies?.find(
                        (wa) => wa.workApplyId === workApplyId
                      )?.applyTime
                    ),
                  workTime:
                    TypeUtils.isNilString(summary.workDateFrom) ||
                    summary.workDateFrom.length === 0 ||
                    TypeUtils.isNilString(summary.workDateTo) ||
                    summary.workDateTo.length === 0
                      ? undefined
                      : `${format(
                          Formatter.createDate(summary.workDateFrom),
                          'HH:mm'
                        )} - ${format(
                          Formatter.createDate(summary.workDateTo),
                          'HH:mm'
                        )}`,
                  workContent: summary.taskIds
                    ?.map(
                      (taskId) =>
                        master.getBy(MasterKey.Tasks, taskId)?.shortName
                    )
                    .join(' '),
                  point: summary.extraFeeIds
                    ?.map(
                      (extraFeeId) =>
                        master.getBy(MasterKey.ExtraFees, extraFeeId)?.shortName
                    )
                    .join(' '),
                  remarks: [
                    summary.beLate ? '遅刻' : undefined,
                    summary.leaveWork ? '早退' : undefined,
                  ].join(' '),
                };
              })
      ),
    [data?.results]
  );
  const columns: ReadonlyArray<Column<DatasourceType>> = React.useMemo(
    () => [
      { Header: '', accessor: 'url' },
      { Header: '就業日', accessor: 'date' },
      { Header: '現場名', accessor: 'matterName' },
      { Header: '依頼元', accessor: 'customerName' },
      { Header: '作業時間', accessor: 'workTime' },
      { Header: '作業内容', accessor: 'workContent' },
      { Header: 'ポイント', accessor: 'point' },
      { Header: '備考', accessor: 'remarks' },
    ],
    []
  );

  return (
    <Wireframe
      title={pageNames.ATTENDANCE_REPORT}
      topMessages={deadlines?.results}
    >
      <VStack width={sizes.width.styledMessageList}>
        <HalfMonthChanger
          applyUnits={applyUnits}
          onChangeLeft={onChangeChanger}
          onChangeRight={onChangeChanger}
        />
        <Skeleton isLoaded={!postAssignedMatterSearch.isLoading} width="100%">
          <StyledTable
            columns={columns}
            customizeRender={({ cell, data: original }) =>
              cell.column.id === 'url' && cell.value !== undefined ? (
                <Icon
                  as={original.isApproved ? FiSearch : FiEdit2}
                  fontSize="md"
                  onClick={() => navigate(cell.value)}
                  // TODO
                />
              ) : cell.column.id === 'matterName' ? (
                <ClickableText
                  hasLink={!TypeUtils.isNilNumber(original.matterId)}
                  onClick={() => {
                    if (!TypeUtils.isNilNumber(original.matterId)) {
                      setSelectedMatterId(original.matterId);
                      onOpen();
                    }
                  }}
                >
                  {cell.render('Cell')}
                </ClickableText>
              ) : (
                <>{cell.render('Cell')}</>
              )
            }
            data={datasource}
          />
          <Divider />
          <Text
            fontSize={sizes.fontSize.rawMessage}
            textAlign="left"
            width="100%"
          >
            勤怠申請 (
            {Formatter.range(
              halfMonthState.year,
              halfMonthState.month - 1,
              halfMonthState.half === 1
                ? master.applyUnit.attendance1
                : master.applyUnit.attendance2
            )}
            )
          </Text>
          <CheckArea
            additionalArea={
              <VStack fontSize={sizes.fontSize.listMessage}>
                <Text color="red" fontWeight="bold">
                  !!! 注意 !!!
                  一度確定すると原則訂正できません．必ず入力内容を確認の上、間違いのないことを確認してから確定してください
                </Text>
              </VStack>
            }
            button={DecideButton}
            buttonProps={{ isDisabled: !checked }}
            confirmMessage="勤怠申請の確定に同意します"
            doneMessage={
              TypeUtils.isNonNil(workApply)
                ? TypeUtils.isNonNilString(workApply.approveTime)
                  ? '承認済です'
                  : '申請済です'
                : undefined
            }
            hidden={datasource.length === 0}
            hiddenMessage="報告するデータがありません"
            onClick={() => {
              actions.setGuard(true);
              postWorkApplyApply.mutate(
                {
                  yearMonth: `${halfMonthState.year}-${halfMonthState.month
                    .toString()
                    .padStart(2, '0')}-01`,
                  userCodeWorker: state.userInfo?.user_code,
                  applyUnitId:
                    halfMonthState.half === 1
                      ? master.applyUnit.attendance1?.id
                      : master.applyUnit.attendance2?.id,
                },
                {
                  onSuccess: () => {
                    refetchDeadlines();
                    navigate();
                    actions.setGuard(false);
                  },
                  onError: (error) => {
                    toast(
                      error instanceof AppError ? error : '確定に失敗しました'
                    );
                    actions.setGuard(false);
                  },
                }
              );
            }}
          />
        </Skeleton>
        <HStack width="100%">
          <BackButton onClick={() => navigate(-1)} />
        </HStack>
      </VStack>
      <MatterDetailModal
        isOpen={isOpen}
        matterId={selectedMatterId}
        onClose={() => {
          setSelectedMatterId(undefined);
          onClose();
        }}
      />
    </Wireframe>
  );
};

export default AttendanceReport;
